import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useStreaming } from "../context/StreamingContext";
import "./Response.css";
// import InfoBar from '../components/InfoBar';
import CollapsibleSidebar from "../components/CollapsibleSidebar";
import RecommendationCard from "../components/RecommendationCard";
import CommonOrdersPanel from "../components/CommonOrdersPanel";

const Response: React.FC = () => {
  const navigate = useNavigate();
  const {
    streamedData,
    parsedData,
    clinicalCaseData,
    isStreaming,
    question,
    conversationMessages,
    currentStreamingMessage,
    addUserMessage,
    setCurrentStreamingMessage,
    addAIMessage,
    setIsStreaming,
    resetStream,
    clinicalNotes,
    followUpQuestions,
    isLoadingFollowUp,
    setFollowUpQuestions,
    setIsLoadingFollowUp,
    setConversationMessages,
    getAbortController,
  } = useStreaming();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const questionDropdownRef = useRef<HTMLDivElement>(null);
  const summaryDropdownRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        questionDropdownRef.current &&
        !questionDropdownRef.current.contains(event.target as Node)
      ) {
        setIsQuestionExpanded(false);
      }
      if (
        summaryDropdownRef.current &&
        !summaryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSummaryExpanded(false);
      }
    };

    if (isQuestionExpanded || isSummaryExpanded) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isQuestionExpanded, isSummaryExpanded]);

  const handleNewChat = () => {
    resetStream();
    navigate("/");
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleQuestionExpanded = () => {
    setIsQuestionExpanded(!isQuestionExpanded);
    if (!isQuestionExpanded) {
      setIsSummaryExpanded(false);
    }
  };

  const toggleSummaryExpanded = () => {
    setIsSummaryExpanded(!isSummaryExpanded);
    if (!isSummaryExpanded) {
      setIsQuestionExpanded(false);
    }
  };

  const handleSendMessage = async (messageToUse?: string) => {
    const userMessage = messageToUse || replyMessage;
    if (!userMessage.trim() || isStreaming) return;

    if (!messageToUse) {
      addUserMessage(userMessage);
      setReplyMessage("");
    }

    // Clear old follow-up questions when sending a new message
    setFollowUpQuestions([]);

    setIsStreaming(true);
    setCurrentStreamingMessage("");
    let completeMessage = "";

    const abortController = getAbortController();
    const signal = abortController.signal;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/ask-specialist-streamed`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            question: userMessage,
          }),
          signal,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      if (reader) {
        let accumulatedText = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // When streaming is complete, add the final message to conversation
            if (completeMessage) {
              addAIMessage(completeMessage);
            }
            setCurrentStreamingMessage("");
            setIsStreaming(false);

            // Fetch follow-up questions after streaming completes
            setIsLoadingFollowUp(true);
            try {
              const followUpResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/followup-questions`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  credentials: "include",
                  signal,
                },
              );

              if (followUpResponse.ok) {
                const followUpData = await followUpResponse.json();
                if (Array.isArray(followUpData)) {
                  setFollowUpQuestions(followUpData);
                } else if (
                  followUpData.questions &&
                  Array.isArray(followUpData.questions)
                ) {
                  setFollowUpQuestions(followUpData.questions);
                }
              }
            } catch (error) {
              console.error("Error fetching follow-up questions:", error);
            } finally {
              setIsLoadingFollowUp(false);
            }
            break;
          }

          if (value) {
            buffer += decoder.decode(value, { stream: true });

            // Process any complete SSE messages in the buffer
            const parts = buffer.split("\n\n");
            buffer = parts.pop() || ""; // Keep any incomplete chunk in buffer

            for (const part of parts) {
              const lines = part.split("\n");
              for (const line of lines) {
                if (line.startsWith("data:")) {
                  const jsonStr = line.replace("data:", "").trim();
                  try {
                    const parsed = JSON.parse(jsonStr);
                    // console.log('Parsed SSE data from ask-specialist:', parsed);

                    // Handle the new chunked format
                    if (parsed.type === "start") {
                      // Reset accumulated text on start
                      accumulatedText = "";
                      console.log("Ask-specialist streaming started");
                    } else if (parsed.type === "text" && parsed.chunk) {
                      // Accumulate text chunks
                      accumulatedText += parsed.chunk;
                      completeMessage = accumulatedText;

                      // Update streaming message progressively
                      setCurrentStreamingMessage(accumulatedText);
                    } else if (parsed.type === "done") {
                      console.log("Ask-specialist streaming completed");
                    } else {
                      // Fallback: handle old format for backward compatibility
                      if (parsed.summaryResponse) {
                        completeMessage = parsed.summaryResponse;
                        setCurrentStreamingMessage(parsed.summaryResponse);
                      }
                    }
                  } catch (err) {
                    console.warn("Invalid JSON in data:", err);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log("Chat message streaming aborted");
        return;
      }
      console.error("Error sending message:", error);
      addAIMessage(
        "An error occurred while processing your request. Please try again.",
      );
      setIsStreaming(false);
    }
  };

  const handleReplyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRegenerate = () => {
    if (isStreaming) return;

    const userMessages = conversationMessages.filter((m) => m.type === "user");
    const lastPrompt =
      userMessages.length > 0
        ? userMessages[userMessages.length - 1].content
        : question;

    if (
      conversationMessages.length > 0 &&
      conversationMessages[conversationMessages.length - 1].type === "ai"
    ) {
      setConversationMessages((prev) => prev.slice(0, -1));
    }

    handleSendMessage(lastPrompt);
  };

  const handleQuestionClick = (question: string) => {
    setReplyMessage(question);
    // Focus the input after setting the message
    setTimeout(() => {
      replyInputRef.current?.focus();
    }, 0);
  };

  // Extract data from parsedData and construct patient summary from clinical case data
  const patientSummary =
    parsedData?.basicPatientSummary ||
    (clinicalCaseData
      ? [
        {
          field: "Age",
          value:
            clinicalCaseData.patient_age != null
              ? `${clinicalCaseData.patient_age} years`
              : "Not specified",
        },
        {
          field: "Gender",
          value: clinicalCaseData.patient_gender || "Not specified",
        },
        {
          field: "ICD-10 Code",
          value: clinicalCaseData.icd10_code || "Pending...",
        },
        ...(clinicalCaseData.rationale
          ? [{ field: "Rationale", value: clinicalCaseData.rationale }]
          : []),
      ]
      : []);

  const templateData = parsedData?.populatedTemplate;
  const aiResponse =
    parsedData?.specialistAIResponse?.summaryResponse || streamedData;

  // console.log('Parsed Data:', parsedData);
  // console.log('Specialist Summary:', specialistSummary);
  // console.log('Patient Summary:', patientSummary);
  // console.log('Template Data:', parsedData?.populatedTemplate);
  // console.log('AI Response:', aiResponse);
  // console.log('Raw Streamed Data:', streamedData);
  // console.log('Is Streaming:', isStreaming);

  return (
    <div className="response-container">
      {/* Header */}
      <header className="response-header">
        <header className="header-text-container">
          <div className="brand">SAGE</div>
        </header>
        <div className="header-center" ref={questionDropdownRef}>
          <div className="question-wrapper">
            <h1 className="main-question" onClick={toggleQuestionExpanded}>
              {question || "Clinical Question"}
            </h1>
            {question && question.length > 50 && (
              <button
                className="question-expand-button"
                onClick={toggleQuestionExpanded}
                aria-label={
                  isQuestionExpanded ? "Collapse question" : "Expand question"
                }
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={isQuestionExpanded ? "rotated" : ""}
                >
                  <path
                    d="M2.5 4.5L6 8L9.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
          {isQuestionExpanded && (
            <div className="question-dropdown">
              <div className="question-dropdown-content">
                {question || "Clinical Question"}
              </div>
            </div>
          )}
          <div className="specialist-summary-section">
            {parsedData?.specialistSummary ? (
              <>
                <div className="summary-wrapper" ref={summaryDropdownRef}>
                  <p
                    className="specialist-summary"
                    onClick={toggleSummaryExpanded}
                  >
                    {parsedData.specialistSummary}
                  </p>
                  {parsedData.specialistSummary.length > 10 && (
                    <button
                      className="summary-expand-button"
                      onClick={toggleSummaryExpanded}
                      aria-label={
                        isSummaryExpanded
                          ? "Collapse summary"
                          : "Expand summary"
                      }
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M8 7v4M8 5h.01"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {isSummaryExpanded && (
                  <div className="summary-dropdown">
                    <div className="summary-dropdown-content">
                      {parsedData.specialistSummary}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="specialist-summary skeleton-text">
                <span
                  className="skeleton-line-inline"
                  style={{ width: "60%" }}
                ></span>
              </p>
            )}
          </div>
        </div>
        <div className="header-right">
          <button className="new-chat-btn" onClick={handleNewChat}>
            <span className="plus-sign">+</span> New Chat
          </button>
          {/* <button className="cart-btn" aria-label="Cart">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.00004 7.33333V10M9.33337 8.66666H6.66671M10.6667 3.99999V2.66666C10.6667 2.31304 10.5262 1.9739 10.2762 1.72385C10.0261 1.4738 9.687 1.33333 9.33337 1.33333H6.66671C6.31309 1.33333 5.97395 1.4738 5.7239 1.72385C5.47385 1.9739 5.33337 2.31304 5.33337 2.66666V3.99999M12 3.99999V13.3333M4.00004 3.99999V13.3333M2.66671 3.99999H13.3334C14.0698 3.99999 14.6667 4.59695 14.6667 5.33333V12C14.6667 12.7364 14.0698 13.3333 13.3334 13.3333H2.66671C1.93033 13.3333 1.33337 12.7364 1.33337 12V5.33333C1.33337 4.59695 1.93033 3.99999 2.66671 3.99999Z" stroke="#000000" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            Orders
          </button> */}
        </div>
      </header>

      {/* Info Bar */}
      {/* <InfoBar
        age={clinicalCaseData?.patient_age}
        gender={clinicalCaseData?.patient_gender}
        icd10Code={clinicalCaseData?.icd10_code}
        rationale={clinicalCaseData?.rationale}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={toggleSidebar}
        isLoading={isStreaming}
            /> */}

      {/* Main Layout */}
      <div className="main-layout">
        {/* Left Sidebar */}
        <CollapsibleSidebar
          isOpen={isSidebarOpen}
          patientData={patientSummary}
          templateData={templateData}
          isLoading={isStreaming}
        />

        {/* Main Content */}
        <main className="main-content">
          <div className="content-wrapper">
            <RecommendationCard
              initialQuestion={question}
              content={aiResponse}
              isLoading={isStreaming && conversationMessages.length === 0}
              conversationMessages={conversationMessages}
              currentStreamingMessage={currentStreamingMessage}
              relatedQuestions={followUpQuestions}
              isLoadingFollowUp={isLoadingFollowUp}
              onQuestionClick={handleQuestionClick}
              onRegenerate={handleRegenerate}
            />
          </div>

          {/* Reply Input */}
          <div className="reply-section">
            <div className="reply-input-container">
              <input
                ref={replyInputRef}
                type="text"
                placeholder="Reply to SAGE"
                className="reply-input"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                onKeyDown={handleReplyKeyDown}
              />
              <button
                className="send-button"
                onClick={() => handleSendMessage()}
                disabled={!replyMessage.trim()}
                aria-label="Send message"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            </div>
            <p className="disclaimer">
              SAGE is a research product and should be used with a healthcare
              professional only. ©{new Date().getFullYear()} Stanford HealthKit
              Lab.
            </p>
          </div>
        </main>

        {/* Right Panel - Common Orders */}
        <aside className="common-orders-aside">
          <CommonOrdersPanel onQuestionClick={handleQuestionClick} />
        </aside>
      </div>
    </div>
  );
};

export default Response;
